import { useMemo, useState } from 'react'
import { useGroupReorder } from '../../hooks/useGroupReorder.js'
import { useGroupReorderBrief } from '../../hooks/useGroupReorderBrief.js'
import { AGENT_LABELS } from '../../lib/agents.js'
import KitchenLoader from '../KitchenLoader.jsx'
import AgentRanBadge from '../AgentRanBadge.jsx'
import ReorderRow from '../reorder/ReorderRow.jsx'
import { Skeleton, SkeletonList } from '../Skeleton.jsx'
import { formatMoney } from '../../lib/format.js'

// CombinedReorderPipeline — the cross-unit consolidated reorder overlay
// (Ship 6). Rendered from GroupDashboardScreen when view === 'reorder'.
//
// TWO decoupled data paths (mirrors GroupMenuEngineering):
//   • useGroupReorder — the below-par item list (service-role read of the
//     group's units). The list ALWAYS renders from these, regardless of
//     the AI brief's phase.
//   • useGroupReorderBrief — the AI "this week" reorder paragraph (cached
//     brief + edge fn). Loading / empty / failed here NEVER blocks the list.
//
// House rule (React #310): ALL hooks fire BEFORE any conditional return.

const UNIT_COLORS = [
  { bg: '#EFF6FF', text: '#1D4ED8', dot: '#3B82F6' }, // blue
  { bg: '#FEF3F2', text: '#B42318', dot: '#F04438' }, // red
  { bg: '#ECFDF3', text: '#027A48', dot: '#12B76A' }, // green
  { bg: '#FFFAEB', text: '#B54708', dot: '#F79009' }, // amber
  { bg: '#F4F3FF', text: '#5925DC', dot: '#7A5AF8' }, // violet
  { bg: '#FDF2FA', text: '#C11574', dot: '#EE46BC' }, // pink
  { bg: '#F0F9FF', text: '#026AA2', dot: '#0BA5EC' }, // cyan
]

const LOADER_MESSAGES = [
  'Reading each location’s shelves…',
  'Tallying what’s below par…',
  'Finding what to order first…',
  'Writing your reorder brief…',
]

export default function CombinedReorderPipeline({ groupId, onBack }) {
  // --- All hooks BEFORE any conditional return (React #310). ---
  const { items, perUnit, units, totals, loading, error, refresh } = useGroupReorder(groupId)
  const { phase, brief, generatedAt, regenerate } = useGroupReorderBrief(groupId)
  const [unitFilter, setUnitFilter] = useState('all')   // 'all' | 'critical' | <restaurant_id>
  const [viewMode, setViewMode]     = useState('flat')  // 'flat' | 'grouped'

  // Stable color per unit, by index in the (name-sorted) units list.
  const unitColorById = useMemo(() => {
    const m = new Map()
    units.forEach((u, idx) => m.set(u.restaurant_id, UNIT_COLORS[idx % UNIT_COLORS.length]))
    return m
  }, [units])

  // Rows visible after the active filter. 'critical' shows only critical
  // items; a restaurant_id narrows to that one unit; 'all' is everything.
  const filteredItems = useMemo(() => {
    if (unitFilter === 'all') return items
    if (unitFilter === 'critical') return items.filter((it) => it.critical === true)
    return items.filter((it) => it.restaurant_id === unitFilter)
  }, [items, unitFilter])

  // Flat list — already urgency-sorted by the edge fn; keep that order.
  const flatRows = filteredItems

  // Grouped — bucket the filtered rows by unit, preserving units order.
  const groupedRows = useMemo(() => {
    const byUnit = new Map()
    for (const it of filteredItems) {
      const arr = byUnit.get(it.restaurant_id) || []
      arr.push(it)
      byUnit.set(it.restaurant_id, arr)
    }
    const out = []
    for (const u of units) {
      const arr = byUnit.get(u.restaurant_id)
      if (arr && arr.length > 0) out.push({ unit: u, items: arr })
    }
    return out
  }, [filteredItems, units])

  const briefText = brief?.brief_text || ''

  // ---- Early returns (all hooks above). ----
  if (loading) {
    return (
      <div className="mx-auto max-w-screen-sm px-4 py-6">
        <BackButton onBack={onBack} />
        <section className="mt-3 rounded-2xl bg-charcoal-hero p-5 shadow-sm">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="mt-3 h-4 w-full" />
          <Skeleton className="mt-2 h-4 w-5/6" />
        </section>
        <section className="mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
          <Skeleton className="h-3 w-36" />
          <SkeletonList rows={4} className="mt-4" />
        </section>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto max-w-screen-sm px-4 py-6">
        <BackButton onBack={onBack} />
        <section className="mt-3 rounded-2xl border border-red-200 bg-red-50/40 p-4 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-red-900/70">
            Group reorder
          </div>
          <div className="mt-2 text-sm text-red-800">{error}</div>
          <button
            type="button"
            onClick={refresh}
            className="mt-2 rounded-md px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </section>
      </div>
    )
  }

  const nothingBelowPar = totals.total_items_below_par === 0

  return (
    <div className="mx-auto max-w-screen-sm px-4 py-6">
      <BackButton onBack={onBack} />

      {/* ---------- AI "this week" reorder brief (charcoal card) ---------- */}
      <section
        className="relative mt-3 rounded-2xl bg-charcoal-hero p-5 shadow-sm"
        aria-label={AGENT_LABELS.groupReorderBrief}
      >
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-[11px] font-medium text-charcoal-text-secondary">
            {AGENT_LABELS.groupReorderBrief}
          </div>
          <div className="flex items-baseline gap-2">
            {phase === 'ready' && (
              <AgentRanBadge generatedAt={generatedAt} tone="dark" />
            )}
            {(phase === 'ready' || phase === 'empty' || phase === 'error') && (
              <button
                type="button"
                onClick={() => regenerate({ bypassCache: true })}
                className="text-[11px] text-charcoal-text-secondary hover:text-white"
                aria-label="Refresh reorder brief"
              >
                Refresh
              </button>
            )}
          </div>
        </div>

        {/* Generating — KitchenLoader, dark tone (never blue spinner). */}
        {phase === 'generating' && (
          <div className="mt-3">
            <KitchenLoader align="row" size="sm" tone="dark" messages={LOADER_MESSAGES} />
          </div>
        )}

        {/* Idle — calm prompt. */}
        {phase === 'idle' && (
          <div className="mt-3">
            <p className="text-[12px] leading-relaxed text-charcoal-text-tertiary">
              Tap to compose your cross-unit reorder brief.
            </p>
            <button
              type="button"
              onClick={() => regenerate({ bypassCache: true })}
              className="mt-2 text-[12px] text-white/90 underline-offset-2 hover:underline"
            >
              Generate
            </button>
          </div>
        )}

        {/* Ready — the one-paragraph brief. */}
        {phase === 'ready' && briefText && (
          <div className="mt-3 text-[15px] leading-relaxed text-white/90">{briefText}</div>
        )}

        {/* Empty / error — deterministic calm copy. The list below still
            renders (decoupled). */}
        {phase === 'empty' && (
          <div className="mt-3 text-[14px] leading-relaxed text-charcoal-text-tertiary">
            {briefText || 'All units are above par on every tracked item. Nothing to order this week.'}
          </div>
        )}
        {phase === 'error' && (
          <div className="mt-3">
            <p className="text-[12px] leading-relaxed text-charcoal-text-tertiary">
              Couldn’t pull your group reorder brief — tap to try again.
            </p>
            <button
              type="button"
              onClick={() => regenerate({ bypassCache: true })}
              className="mt-2 text-[12px] text-white/90 underline-offset-2 hover:underline"
            >
              Retry
            </button>
          </div>
        )}
      </section>

      {/* ---------- The below-par list (decoupled from the brief) -------- */}
      <section className="mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-black/55">
            Below par across your units
          </div>
          <div className="text-[11px] text-black/55">
            {totals.total_items_below_par} item{totals.total_items_below_par === 1 ? '' : 's'}
            {totals.critical_count > 0 && ` · ${totals.critical_count} critical`}
          </div>
        </div>

        {nothingBelowPar ? (
          <p className="mt-3 text-sm text-black/65 leading-snug">
            All units are above par on every tracked item. Nothing to order this week.
          </p>
        ) : (
          <>
            {/* Filter chips: All | each unit | Critical only. */}
            <nav aria-label="Filter reorder list" className="mt-3 overflow-x-auto">
              <div className="inline-flex min-w-full items-center gap-2 pb-1">
                <FilterChip
                  label="All"
                  active={unitFilter === 'all'}
                  onClick={() => setUnitFilter('all')}
                />
                {units.map((u) => (
                  <FilterChip
                    key={u.restaurant_id}
                    label={u.restaurant_name}
                    color={unitColorById.get(u.restaurant_id)}
                    active={unitFilter === u.restaurant_id}
                    onClick={() => setUnitFilter(u.restaurant_id)}
                  />
                ))}
                <FilterChip
                  label="Critical only"
                  active={unitFilter === 'critical'}
                  onClick={() => setUnitFilter('critical')}
                />
              </div>
            </nav>

            {/* View-mode toggle: Flat (default) | Grouped by unit. */}
            <div className="mt-3 inline-flex rounded-lg border border-black/10 p-0.5 text-xs">
              <ModeButton label="Flat" active={viewMode === 'flat'} onClick={() => setViewMode('flat')} />
              <ModeButton
                label="Grouped by unit"
                active={viewMode === 'grouped'}
                onClick={() => setViewMode('grouped')}
              />
            </div>

            {viewMode === 'flat' ? (
              <ul className="mt-3 overflow-hidden rounded-xl border border-black/10">
                {flatRows.map((it, i) => (
                  <ReorderRow
                    key={it.item_id || `${it.restaurant_id}-${i}`}
                    item={it}
                    unitColor={unitColorById.get(it.restaurant_id)}
                  />
                ))}
              </ul>
            ) : (
              <div className="mt-3 space-y-4">
                {groupedRows.map(({ unit, items: unitItems }) => (
                  <div key={unit.restaurant_id}>
                    <div className="mb-1.5 flex items-center gap-2">
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ backgroundColor: unitColorById.get(unit.restaurant_id)?.dot || '#6b7280' }}
                        aria-hidden="true"
                      />
                      <span className="text-[12px] font-semibold text-black/70">
                        {unit.restaurant_name}
                      </span>
                      <span className="text-[11px] text-black/45">
                        {perUnit[unit.restaurant_id]?.items_below_par ?? unitItems.length} below par
                      </span>
                    </div>
                    <ul className="overflow-hidden rounded-xl border border-black/10">
                      {unitItems.map((it, i) => (
                        <ReorderRow
                          key={it.item_id || `${it.restaurant_id}-${i}`}
                          item={it}
                          unitColor={unitColorById.get(it.restaurant_id)}
                        />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Authoritative GROUP-WIDE total footer. Computed straight from
                the snapshot hook's totals (never the AI paragraph), so it
                always matches the snapshot's source numbers. Shown unfiltered
                + explicitly labelled the group total to stay unambiguous when
                a filter is active. */}
            <div
              className="mt-3 flex items-baseline justify-between gap-2 border-t border-black/10 pt-3"
              aria-label="Group reorder total"
            >
              <div className="text-[12px] font-medium text-black/65">
                {totals.total_items_below_par} item
                {totals.total_items_below_par === 1 ? '' : 's'} below par across{' '}
                {units.length} unit{units.length === 1 ? '' : 's'}
              </div>
              <div className="text-[13px] font-semibold text-black/85">
                {formatMoney(totals.total_restock_dollars)} to restock
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  )
}

function FilterChip({ label, color, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors',
        active
          ? 'border-black/20 bg-black/5 font-semibold text-black/85'
          : 'border-black/10 bg-white text-black/60 hover:text-black/80',
      ].join(' ')}
    >
      {color && (
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: color.dot }}
          aria-hidden="true"
        />
      )}
      <span className="max-w-[12rem] truncate">{label}</span>
    </button>
  )
}

function ModeButton({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'rounded-md px-2.5 py-1 font-medium transition-colors',
        active ? 'bg-black/5 text-black/85' : 'text-black/55 hover:text-black/80',
      ].join(' ')}
    >
      {label}
    </button>
  )
}

function BackButton({ onBack }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-1 text-sm font-medium text-black/60 hover:text-black/85"
    >
      ← All units
    </button>
  )
}
